import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Descriptions, Form, Space, Steps } from 'antd';
import type { FormInstance } from 'antd';
import type { CreateStationBody } from '@goelectric/shared-types';
import { buildCreateStationBody, type StationFormState } from '../utils/stationForm';
import { CONNECTOR_STANDARD_LABELS, OCPP_VERSION_LABELS, POWER_TYPE_LABELS } from '../utils/format';
import { StationConnectorsFields } from './StationConnectorsFields';
import { StationIdentityFields } from './StationIdentityFields';
import { StationLocationFields, type StationLocationOption } from './StationLocationFields';

export interface StationFormProps {
  /** Recibe el cuerpo del contrato ya compuesto; la llamada a la API la hace la página. */
  onSubmit: (body: CreateStationBody) => void;
  submitting?: boolean;
  /** Instancia del `Form` para operarla desde afuera (p. ej. marcando el campo de un error). */
  form?: FormInstance<StationFormState>;
  /** Campo que el backend marcó en un error; el wizard salta a su paso si no es el actual. */
  errorField?: string | null;
}

/** Único lugar donde se declara qué campos pertenecen a cada paso (fuente del mapeo campo→paso). */
const STEPS: { title: string; fields: (keyof StationFormState)[] }[] = [
  { title: 'Identidad del cargador', fields: ['id', 'vendor', 'model', 'protocol', 'maxPowerKw'] },
  { title: 'Ubicación', fields: ['locationId'] },
  { title: 'Conectores', fields: ['connectors'] },
  { title: 'Revisión', fields: [] },
];

const LAST_STEP = STEPS.length - 1;

/** Título de un paso por índice (acota el `undefined` del acceso por índice). */
const stepTitle = (index: number): string => STEPS[index]?.title ?? '';

/** Campo → índice de paso, derivado de `STEPS` (el salto por error y el resumen lo reusan). */
const FIELD_STEP = new Map<string, number>(
  STEPS.flatMap((step, index) => step.fields.map((field) => [field as string, index] as const)),
);

/**
 * Formulario de alta de Cargador, en 4 pasos (US-2.2, TG-125): identidad → ubicación →
 * conectores → revisión. Espejo de `LocationForm`: los campos de todos los pasos están
 * **montados** (los inactivos se ocultan con CSS) para que los valores persistan y el
 * resumen reuse `STEPS`. "Siguiente" valida solo el paso actual.
 *
 * No hay chequeo consultivo de unicidad del ChargePoint ID: el contrato no expone endpoint
 * de disponibilidad; la verdad es el índice único de la DB y el POST (`EQUIPO_CHARGEPOINT_DUPLICADO`).
 */
export function StationForm({
  onSubmit,
  submitting = false,
  form: formProp,
  errorField,
}: StationFormProps) {
  const [internalForm] = Form.useForm<StationFormState>();
  const form = formProp ?? internalForm;
  const [current, setCurrent] = useState(0);
  // Ubicación elegida: el host la retiene para el nombre del resumen sin segundo request.
  const [location, setLocation] = useState<StationLocationOption | null>(null);

  const handleFinish = (values: StationFormState) => {
    const body = buildCreateStationBody(values);
    if (body) {
      onSubmit(body);
    }
  };

  // Salto declarativo al paso del campo que marcó el backend (sin refs ni handles).
  useEffect(() => {
    if (!errorField) {
      return;
    }
    const step = FIELD_STEP.get(errorField);
    if (step !== undefined) {
      setCurrent(step);
    }
  }, [errorField]);

  /** Avanza solo si el paso valida; hacia atrás no se valida nada. */
  const goNext = async () => {
    try {
      // `recursive`: al validar el paso de conectores hay que bajar a las reglas de cada
      // fila (unicidad del Nº, coherencia de potencia); el `Form.List` no las cascada solo.
      await form.validateFields(STEPS[current]?.fields ?? [], { recursive: true });
    } catch {
      // El paso no valida: antd ya muestra los errores inline.
      return;
    }
    setCurrent((step) => Math.min(step + 1, LAST_STEP));
  };

  // Submit final: si algún campo de un paso anterior quedó inválido (raro), salta a su paso
  // en vez de fallar en silencio con un botón que "no hace nada".
  const handleFinishFailed = (info: { errorFields: { name: (string | number)[] }[] }) => {
    const first = info.errorFields[0]?.name?.[0];
    const step = typeof first === 'string' ? FIELD_STEP.get(first) : undefined;
    if (step !== undefined) {
      setCurrent(step);
    }
  };

  return (
    <Form
      className="cargadores-form"
      form={form}
      layout="vertical"
      initialValues={{ protocol: 'ocpp2.0.1', connectors: [{}] }}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <div className="cargadores-form__header">
        <Steps
          current={current}
          size="small"
          labelPlacement="vertical"
          items={STEPS.map((step) => ({ title: step.title }))}
          style={{ marginBottom: 12 }}
        />
      </div>

      <div style={{ display: current === 0 ? undefined : 'none' }}>
        <StationIdentityFields />
      </div>

      <div style={{ display: current === 1 ? undefined : 'none' }}>
        <StationLocationFields onSelect={setLocation} />
      </div>

      <div style={{ display: current === 2 ? undefined : 'none' }}>
        <StationConnectorsFields />
      </div>

      {current === LAST_STEP && <StationReview form={form} locationName={location?.name} />}

      <Space style={{ marginTop: 8 }}>
        {current > 0 && <Button onClick={() => setCurrent((step) => step - 1)}>Anterior</Button>}
        {current < LAST_STEP && (
          <Button type="primary" onClick={goNext}>
            Siguiente
          </Button>
        )}
        {current === LAST_STEP && (
          <Button type="primary" htmlType="submit" loading={submitting}>
            Crear cargador
          </Button>
        )}
      </Space>
    </Form>
  );
}

/** Fila del resumen de conectores: `Nº · estándar · tipo · potencia · voltaje · amperaje`. */
function connectorSummary(row: StationFormState['connectors'][number]): string {
  const parts: string[] = [];
  if (row?.standard) parts.push(CONNECTOR_STANDARD_LABELS[row.standard]);
  if (row?.powerType) parts.push(POWER_TYPE_LABELS[row.powerType]);
  if (typeof row?.maxPowerKw === 'number') parts.push(`${row.maxPowerKw} kW`);
  if (typeof row?.maxVoltageV === 'number') parts.push(`${row.maxVoltageV} V`);
  if (typeof row?.maxAmperageA === 'number') parts.push(`${row.maxAmperageA} A`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/**
 * Paso 4: resumen de lo que se va a enviar. Se monta solo en la revisión (no re-renderiza el
 * wizard en cada tecla). Los títulos y la agrupación salen de `STEPS`.
 */
function StationReview({
  form,
  locationName,
}: {
  form: FormInstance<StationFormState>;
  locationName?: string;
}) {
  const values = Form.useWatch([], form) ?? ({} as StationFormState);
  const connectors = values.connectors ?? [];
  const location = locationName ?? values.locationId;

  const sections: { title: string; items: { label: string; value: ReactNode }[] }[] = [
    {
      title: stepTitle(0),
      items: [
        { label: 'ChargePoint ID', value: values.id || '—' },
        { label: 'Marca', value: values.vendor || '—' },
        { label: 'Modelo', value: values.model || '—' },
        {
          label: 'Protocolo',
          value: values.protocol ? OCPP_VERSION_LABELS[values.protocol] : '—',
        },
        {
          label: 'Potencia máx',
          value: typeof values.maxPowerKw === 'number' ? `${values.maxPowerKw} kW` : '—',
        },
      ],
    },
    {
      title: stepTitle(1),
      items: [{ label: 'Ubicación', value: location || '—' }],
    },
    {
      title: stepTitle(2),
      items:
        connectors.length > 0
          ? connectors.map((row, index) => ({
              label: `Conector ${row?.id ?? index + 1}`,
              value: connectorSummary(row),
            }))
          : [{ label: 'Conectores', value: '—' }],
    },
  ];

  return (
    <>
      {sections.map((section) => (
        <Descriptions
          key={section.title}
          title={section.title}
          column={1}
          size="small"
          bordered
          style={{ marginBottom: 16 }}
        >
          {section.items.map((item, index) => (
            <Descriptions.Item key={`${item.label}-${index}`} label={item.label}>
              {item.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ))}
    </>
  );
}
